import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { useAppStore } from "@/stores";
import { NestedCommission, TODO } from "@revelate/types";
import { CommissionCommentWithUser } from "@revelate/types/comments";
import { getCurrencyFormatted } from "@/lib/app";
import { mutate as globalMutate } from "swr";
import {
  getCommissionComments,
  addCommissionComment,
  deleteCommissionComment,
} from "@/lib/supabase/comments";
import { Trash2, Send, CircleUser, Loader2 } from "lucide-react";
import { getFullName, getUserInitials } from "@revelate/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface CommissionCommentsProps {
  commission: NestedCommission;
  openOnRender?: boolean;
  hideText?: boolean;
}

export const CommissionComments = ({
  commission,
  openOnRender = false,
  hideText = false,
}: CommissionCommentsProps) => {
  const { currentUser } = useAppStore((state) => state);
  const [comments, setComments] = useState<CommissionCommentWithUser[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(openOnRender);

  // Define fetchComments before using it in useEffect
  const fetchComments = useCallback(async () => {
    if (!commission.id) return;

    // Only show loading state if there are no comments yet
    if (comments.length === 0) {
      setIsCommentsLoading(true);
    }

    try {
      const result = await getCommissionComments(commission.id);
      if (result) {
        setComments(result);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsCommentsLoading(false);
    }
  }, [commission.id, comments.length]);

  // Fetch comments when dialog opens or when openOnRender changes
  useEffect(() => {
    if (isOpen && commission.id) {
      fetchComments();
    }
  }, [isOpen, commission.id, fetchComments]);

  // Handle openOnRender prop changes
  useEffect(() => {
    if (openOnRender && !isOpen) {
      setIsOpen(true);
    }
  }, [openOnRender, isOpen]);

  // Listen for clicks on the comment icon in the card
  useEffect(() => {
    const handleCommentIconClick = () => {
      // If this component is inside the icon that was clicked, open the dialog
      setIsOpen(true);
    };

    window.addEventListener("commission-comment-added", handleCommentIconClick);

    return () => {
      window.removeEventListener(
        "commission-comment-added",
        handleCommentIconClick
      );
    };
  }, []);

  // Helper function to trigger revalidation of commissions data
  const triggerRevalidation = async () => {
    // Revalidate all commissions data to refresh the report card
    await globalMutate((key) => {
      // Check if the key is an array and the first element is 'commissions'
      return Array.isArray(key) && key[0] === "commissions";
    });
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !currentUser?.id || !commission.id) return;

    setIsLoading(true);
    try {
      await addCommissionComment({
        commission_id: commission.id,
        user_id: currentUser.id,
        comment: newComment.trim(),
      });
      setNewComment("");
      await fetchComments();

      await triggerRevalidation();
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!commentId) return;

    try {
      await deleteCommissionComment(commentId);
      await fetchComments();

      await triggerRevalidation();
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  // Utility functions for handling comment user format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "MMM d, yyyy h:mm a");
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Button
        variant="ghost"
        className="p-0 h-auto font-normal flex items-center gap-1"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      >
        {!hideText && <span>Comment</span>}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Comments</DialogTitle>
            <DialogDescription>
              {commission.deal?.name || "Commission"} -{" "}
              {getCurrencyFormatted(
                commission.amount || 0,
                currentUser?.company?.currency?.code || "USD"
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[300px] overflow-y-auto my-4">
            {isCommentsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="flex items-start gap-3 border-b pb-5 last:border-b-0 last:pb-0 pt-2 hover:bg-muted/20 rounded-sm px-0"
                  >
                    {comment?.user?.avatar_url ? (
                      <Avatar className="h-9 w-9">
                        <AvatarImage
                          src={comment.user.avatar_url}
                          alt={getFullName(comment.user as TODO)}
                        />
                        <AvatarFallback>
                          {getUserInitials(comment.user as TODO)}
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <CircleUser className="h-5 w-5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-0">
                        <p className="font-medium text-sm">
                          {getFullName(comment.user as TODO)}
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-muted-foreground">
                            {formatDate(comment.created_at)}
                          </p>
                          {currentUser?.id === comment.user_id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-destructive"
                              onClick={() => handleDeleteComment(comment.id)}
                              title="Delete comment"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm">{comment.comment}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 px-4 border border-dashed rounded-md border-muted-foreground/30">
                <h3 className="font-medium mb-1">No comments yet</h3>
                <p className="text-muted-foreground text-sm mb-3">
                  Be the first to add a comment to this commission.
                </p>
                <p className="text-xs text-muted-foreground">
                  Comments help track important information and decisions about
                  this commission.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Input
              type="text"
              value={newComment}
              onChange={(e) => {
                // Directly set the value without any transformation
                setNewComment(e.target.value);
              }}
              placeholder="Add a comment..."
              className="flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddComment();
                }
                // FIXME: Spacebar isn't typing normally so
                // temporarily manually adding a space character
                else if (e.key === " ") {
                  setNewComment((prev) => prev + " ")
                }
              }}
            />
            <Button
              onClick={handleAddComment}
              disabled={isLoading || !newComment.trim()}
              className="gap-1"
              title="Send comment"
            >
              {isLoading ? (
                "Sending..."
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span className="hidden sm:inline">Send</span>
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
